// Sign-ins today: successful and failed sign-ins against the login's daily caps, live runs
// against max_runs_per_day, the failed streak toward the automatic turn-off, and when the next
// sign-in is allowed. The spacing is shown, never skipped. An older API sends no limits: the
// counts show without caps.

import React from 'react';
import { Box, Typography } from '@mui/material';
import { BAD, OK, STREAK_MAX, budgetOf, fmtClock } from './deskModel';
import { Meter, useTones } from './tones';

// Compact table cell: "5 ok / 1 failed", a meter, and the streak when there is one.
export const SignInsCell = ({ row }) => {
    const t = useTones();
    const b = budgetOf(row);
    const cap = b.limits?.successful_logins_per_day ?? 10;
    return (
        <Box className="d-font" sx={{ fontSize: 12 }}>
            <span style={{ color: t.ok }}>{b.ok} ok</span>{' / '}
            <span style={{ color: b.failed > 0 ? t.bad : t.muted }}>{b.failed} failed</span>
            {b.inProgress > 0 && <span style={{ color: t.muted }}>{' / '}{b.inProgress} in progress</span>}
            <Box sx={{ display: 'flex', gap: '2px', mt: '3px' }} aria-hidden>
                {Array.from({ length: Math.min(cap, 12) }).map((_, i) => (
                    <Box key={i} component="span" sx={{
                        width: 7, height: 6, borderRadius: '2px',
                        bgcolor: i < b.ok ? t.ok : i < b.ok + b.failed ? t.bad : (t.dark ? 'rgba(255,255,255,0.14)' : '#e3e5e8'),
                    }} />
                ))}
            </Box>
            {b.streak > 0 && <Box sx={{ color: t.bad, mt: 0.25 }}>{b.streak} failed in a row</Box>}
            {b.nextAllowed && <Box sx={{ color: t.muted, mt: 0.25 }}>next sign-in {fmtClock(b.nextAllowed, row.timezone)}</Box>}
        </Box>
    );
};

const Line = ({ label, used, of, colour }) => {
    const t = useTones();
    return (
        <Box sx={{ display: 'grid', gridTemplateColumns: '150px 1fr', alignItems: 'center', gap: 1 }}>
            <Typography className="d-font" sx={{ fontSize: 13 }}>{label}{of ? ` ${used} of ${of}` : ` ${used}`}</Typography>
            {of ? <Meter used={used} of={of} colour={colour} label={label} /> : <Box sx={{ color: t.muted }} />}
        </Box>
    );
};

// office: Test runs are allowed here, so the note says so when live runs are used up.
const BudgetMeter = ({ row, office, nowMs = Date.now() }) => {
    const t = useTones();
    const b = budgetOf(row, nowMs);
    const out = b.liveLeft === 0;
    const note = [
        out ? `Live runs today: ${b.live} of ${b.limits.max_runs_per_day}. Another live run is refused until tomorrow${office ? '; a test run is still allowed' : ''}.`
            : b.nextAllowed ? `Next sign-in allowed at ${fmtClock(b.nextAllowed, row.timezone)} (in ${b.waitMin} min): a run that must sign in before then waits.`
                : 'Next sign-in allowed now.',
        b.streak !== null ? `Failed in a row: ${b.streak} of ${STREAK_MAX} (${STREAK_MAX} turns the login off).` : null,
        office ? 'Test runs use this same account budget.' : null,
    ].filter(Boolean).join(' ');
    return (
        <Box data-budget sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            <Line label="Successful" used={b.ok} of={b.limits?.successful_logins_per_day} colour={OK} />
            <Line label="Failed" used={b.failed} of={b.limits?.login_attempts_per_day} colour={BAD} />
            <Line label="Live runs" used={b.live} of={b.limits?.max_runs_per_day} colour={OK} />
            {b.inProgress > 0 && <Typography className="d-font" sx={{ fontSize: 12, color: t.muted }}>Signing in right now.</Typography>}
            <Typography className="d-font" sx={{
                fontSize: 12, mt: 0.75, p: 1, borderRadius: 1,
                bgcolor: out ? (t.dark ? 'rgba(255,183,77,0.12)' : '#fff4e5') : (t.dark ? 'action.hover' : '#f5f6f8'),
                color: out ? t.warn : 'text.primary',
            }}>
                {note}
            </Typography>
        </Box>
    );
};

export default BudgetMeter;
